import {
  Badge,
  Button,
  Progress,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  TableProps,
  TableCellProps,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { SortOrder } from '@duke-hq/lib/pagination';
import { formatNumber } from '@duke-hq/lib/numberFormats';
import {
  DataTableCell,
  DataTableCellAction,
  DataTableColumnAlign,
  DataTableSchema,
} from './schema';
import { useCallback, useState } from 'react';

function alignTdProps(align?: DataTableColumnAlign): TableCellProps {
  if (align === 'right') {
    return { isNumeric: true };
  }
  if (align === 'center') {
    return { textAlign: 'center' };
  }
  return {};
}

// A Data driven table
//
// The data and functionality of this table is mostly driven by the data provided
// to it. It has a well-defined schema, with multiple cell renderers to cover
// most cases. You can override the cell rendering for some or all cells.
// Cells in the same column do not need to be of the same type.
//
// Sorting and filtering must be implemented by the function that provides
// the data.
//
// You can pass callbacks to list for sorting and filtering requests.
const compactSx = {
  tr: { h: 8 },
  'th, td': { px: 1, py: 0 },
  'th:first-of-type, td:first-of-type': { pl: 4 },
};

export function DataTable<T = unknown>({
  tableProps,
  compact,
  data,
  onSortChange,
  renderOverrideCell,
  handleAction,
  onRowClick,
}: {
  tableProps?: TableProps;
  compact?: boolean;

  data: DataTableSchema<T>;
  onSortChange: (columnName: string, direction?: SortOrder) => void;

  // Optional function to override cell rendering, if this function returns
  // undefined, the default cell rendering will be used.
  //
  // ```ts
  // const renderOverrideCell = useCallback((cell: DataTableCell) => {
  //   if (typeof cell === 'object' && 'type' in cell && cell.type === 'custom') {
  //     if (
  //       typeof cell.data === 'object' &&
  //       cell.data !== null &&
  //       'label' in cell.data &&
  //       typeof cell.data.label === 'string'
  //     ) {
  //       return <div>custom: {cell.data.label}</div>;
  //     }
  //     return undefined;
  //   }
  // }, []);
  // ```
  renderOverrideCell?: (cell: DataTableCell<T>) => undefined | React.ReactNode;

  // Optional function to handle cell actions, if this function is provided,
  // the when the action cell is clicked, this function will be called.
  handleAction?: (action: DataTableCellAction<T>) => Promise<void>;

  // Optional row click handler. When provided, rows become clickable (pointer
  // cursor and hover highlight); cells with their own controls stop the event
  // from reaching the row.
  onRowClick?: (rowId: string | number) => void;
}): React.ReactElement {
  return (
    <Table {...tableProps} sx={{ ...(compact ? compactSx : undefined), ...tableProps?.sx }}>
      <Thead>
        <Tr>
          {Object.entries(data.headers).map(([name, header]) => {
            const label = header.label ?? <pre>{name}</pre>;
            const alignProps = alignTdProps(header.align);

            if ('sortable' in header && header.sortable) {
              return (
                <Th
                  key={name}
                  {...alignProps}
                  onClick={() => {
                    switch (header.currentSortOrder) {
                      case SortOrder.ASC:
                        onSortChange(name, SortOrder.DESC);
                        break;
                      case SortOrder.DESC:
                        onSortChange(name, undefined);
                        break;
                      case undefined:
                        onSortChange(name, SortOrder.ASC);
                        break;
                      default:
                        onSortChange(name, SortOrder.ASC);
                    }
                  }}
                  cursor="pointer"
                >
                  {label}
                </Th>
              );
            } else {
              return (
                <Th key={name} {...alignProps}>
                  {label}
                </Th>
              );
            }
          })}
        </Tr>
      </Thead>
      <Tbody>
        {data.rows.map((row) => (
          <Tr
            key={row.id}
            onClick={onRowClick == null ? undefined : () => onRowClick(row.id)}
            // Keyboard parity for clickable rows: without this the row's target
            // is reachable by mouse only.
            role={onRowClick == null ? undefined : 'button'}
            tabIndex={onRowClick == null ? undefined : 0}
            onKeyDown={
              onRowClick == null
                ? undefined
                : (event) => {
                    // Only the row's own key presses: a link or action button
                    // inside a cell owns its Enter, and the keypress would
                    // otherwise bubble here and navigate twice.
                    if (event.target !== event.currentTarget) {
                      return;
                    }
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onRowClick(row.id);
                    }
                  }
            }
            cursor={onRowClick == null ? undefined : 'pointer'}
            _hover={onRowClick == null ? undefined : { bg: 'bg.subtle' }}
          >
            {Object.entries(row.cells).map(([name, cell]) => (
              <Cell
                key={name}
                cell={cell}
                align={data.headers[name]?.align}
                compact={compact}
                renderOverrideCell={renderOverrideCell}
                handleAction={handleAction}
              />
            ))}
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}

function Cell<T = unknown>({
  cell,
  align,
  compact,
  renderOverrideCell,
  handleAction,
}: {
  cell: DataTableCell<T>;
  align?: DataTableColumnAlign;
  compact?: boolean;
  renderOverrideCell?: (cell: DataTableCell<T>) => undefined | React.ReactNode;
  handleAction?: (action: DataTableCellAction<T>) => Promise<void>;
}) {
  const tdProps = alignTdProps(align);

  if (typeof renderOverrideCell === 'function') {
    const overrideCell = renderOverrideCell(cell);
    if (overrideCell !== undefined) {
      return overrideCell;
    }
  }

  if (cell === undefined) {
    return <Td {...tdProps}>-</Td>;
  }

  if (typeof cell === 'string') {
    return <Td {...tdProps}>{cell}</Td>;
  }

  if (typeof cell === 'number') {
    return <Td {...tdProps}>{cell}</Td>;
  }

  if (typeof cell === 'boolean') {
    return <Td {...tdProps}>{cell ? 'Yes' : 'No'}</Td>;
  }

  if (typeof cell === 'object') {
    if ('type' in cell) {
      switch (cell.type) {
        case 'raw':
          return (
            <Td {...tdProps}>
              <code>{cell.value}</code>
            </Td>
          );
        case 'number':
          return (
            <Td {...tdProps} isNumeric>
              <code>{cell.value === null ? '—' : formatNumber(cell.value)}</code>
            </Td>
          );
        case 'progress':
          return (
            <Td {...tdProps}>
              {cell.isIndeterminate === true ? (
                <Progress
                  hasStripe={cell.hasStripe ?? false}
                  isIndeterminate={cell.isIndeterminate ?? false}
                />
              ) : (
                <Progress
                  value={cell.value}
                  max={cell.max ?? 100}
                  min={cell.min ?? 0}
                  hasStripe={cell.hasStripe ?? false}
                  isIndeterminate={cell.isIndeterminate ?? false}
                />
              )}
            </Td>
          );
        case 'status':
          return (
            <Td {...tdProps}>
              <Badge colorScheme={cell.colorScheme ?? 'gray'} variant={cell.variant ?? 'solid'}>
                {cell.label}
              </Badge>
            </Td>
          );
        case 'link':
          return (
            <Td {...tdProps}>
              <Link
                to={cell.href}
                target={cell.newTab === true ? '_blank' : '_self'}
                // The link owns this click: a clickable row must not also fire.
                onClick={(event) => event.stopPropagation()}
                style={{ color: 'var(--chakra-colors-blue-400)', textDecoration: 'underline' }}
              >
                {cell.label}
              </Link>
            </Td>
          );
        case 'date': {
          if (typeof cell.label === 'string') {
            return <Td {...tdProps}>{cell.label}</Td>;
          }
          if (typeof cell.label === 'number') {
            return <Td {...tdProps}>{new Date(cell.label).toLocaleDateString()}</Td>;
          }
          break;
        }
        case 'action': {
          return (
            <ActionCell cell={cell} align={align} compact={compact} handleAction={handleAction} />
          );
        }
        case 'custom': {
          return (
            <Td {...tdProps}>
              <pre>missing custom cell renderer</pre>
            </Td>
          );
        }
      }
    }
  }
}

function ActionCell<T = unknown>({
  cell,
  align,
  compact,
  handleAction,
}: {
  cell: DataTableCellAction<T>;
  align?: DataTableColumnAlign;
  compact?: boolean;
  handleAction?: (action: DataTableCellAction<T>) => Promise<void>;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const tdProps = alignTdProps(align);

  const handleClick = useCallback(
    async (event: React.MouseEvent) => {
      // The button owns this click: a clickable row must not also fire.
      event.stopPropagation();
      if (typeof handleAction === 'function') {
        setIsLoading(true);
        await handleAction(cell);
        setIsLoading(false);
      } else {
        console.error(
          'handleAction prop for DataTable is either undefined or not a function',
          handleAction,
        );
      }
    },
    [cell, handleAction],
  );

  return (
    <Td {...tdProps}>
      <Button
        colorScheme={cell.colorScheme}
        isActive={cell.isActive}
        isDisabled={cell.isDisabled}
        isLoading={isLoading}
        size={compact ? 'xs' : cell.size}
        variant={cell.variant}
        onClick={handleClick}
      >
        {cell.label}
      </Button>
    </Td>
  );
}
